import Card from "components/Card";
import Button from "components/Button";
import { fmtDate, fmtCurrencyCompact } from "utils";
import { getPlanCategory, PlanCategory, isYearlyPlan, getTrialInfo, getDiscountPriceFor } from "utils/planUtils";
import { formatSubscriptionPrice, calculateBillingPeriod } from "../tabs/billingDataTransformers";
import SubscriptionAlertBanner from "./SubscriptionAlertBanner";
import { useUser } from "routes/dataroutes/UserData";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useBillingNavigation } from "../hooks/useBillingNavigation";
import LoadingCard from "../components/LoadingCard";
import { PureTooltip } from "components/Tooltip";
import { trackBillingV2ResubscribeClicked } from "utils/Tracking";
import { isGrandfatheredPlan } from "utils/legacyRate";
import { LegacyPricingNote } from "components/LegacyRateCallout";


export default function CurrentPlanCard() {
  const { navigate } = useBillingNavigation();
  const user = useUser();
  const { activeSubscription, activeSubscriptionFetcher } = useActiveSubscription();
  const { pricingOptions } = usePricingOptions();

  // Determine plan category
  const planCategory: PlanCategory = getPlanCategory(
    activeSubscription?.planSlug,
    user?.isFreeTrial || false,
  );

  // Format renewal date
  const renewalDate = activeSubscription?.currentTermEnd
    ? fmtDate(new Date(activeSubscription.currentTermEnd * 1000))
    : null;


  // Calculate price with coupon discounts applied locally
  const { activeCoupons } = useActiveCoupons();
  const discountedPrice = getDiscountPriceFor(activeSubscription, activeCoupons);
  const hasDiscount = activeSubscription?.price && discountedPrice < activeSubscription.price;

  const formattedPrice = formatSubscriptionPrice(
    discountedPrice ?? activeSubscription?.price,
    activeSubscription?.currencyCode,
  );
  const originalPrice = hasDiscount
    ? fmtCurrencyCompact(activeSubscription.price / 100, activeSubscription.currencyCode)
    : null;

  const isFreePlan = activeSubscription?.planSlug === "free";

  // Legacy pricing note (ENG-1112). Needs user.newMultipliersEnabled from scraperapi-server #937; nothing shows while undefined.
  const showLegacyPricingNote = isGrandfatheredPlan(user?.newMultipliersEnabled, planCategory);

  const billingPeriod = calculateBillingPeriod(
    activeSubscription?.billingPeriod,
    activeSubscription?.billingPeriodUnit,
  );

  // Check subscription status for alerts
  const scheduledPlanSlug = activeSubscription?.scheduledSubscription?.planSlug;
  const isCancelled = scheduledPlanSlug === "free";
  const isTrialEnding = user?.isFreeTrial;
  const trialInfo = isTrialEnding ? getTrialInfo(user?.signedUpAt) : null;

  // Determine renewal message based on plan category and scheduled changes
  const getRenewalMessage = () => {
    // Check for scheduled subscription change (downgrade/upgrade pending)
    const scheduledSub = activeSubscription?.scheduledSubscription;
    if (scheduledSub && renewalDate) {
      // Skip showing message for cancellations - the banner already shows "Benefits end on [date]"
      if (scheduledSub.planSlug === "free") {
        return null;
      }
      return `Plan changes to ${scheduledSub.planName} on ${renewalDate}`;
    }

    if (planCategory === "free-trial" && trialInfo) {
      return trialInfo.daysRemaining > 0
        ? `Your trial ends in ${trialInfo.daysRemaining} day${trialInfo.daysRemaining !== 1 ? "s" : ""}`
        : null;
    }
    if (planCategory === "free" && renewalDate) {
      return `Plan renews on ${renewalDate}`;
    }
    if (renewalDate) {
      return `Plan renews on ${renewalDate}`;
    }
    return null;
  };

  const renewalMessage = getRenewalMessage();

  const handleResubscribeClick = () => {
    const planSlug = activeSubscription?.planSlug || "";
    // TODO in case of no active subscription (cannot happen) this will still send a tracking event and will navigate to a page which does not exits?
    trackBillingV2ResubscribeClicked(planSlug);
    navigate(`plans/resubscribe/${planSlug}`, { viewTransition: true });
  };

  const handleManagePlanClick = () => navigate("../manage");

  // Prefer activeSubscription (fresh from Chargebee on every mutation) over user.planName,
  // which lags behind until the Chargebee subscription_changed webhook updates the DB row.
  // Free users have no activeSubscription, so user.planName is the natural fallback.
  const currentPlanName = activeSubscription?.planName;

  return (
    <LoadingCard isLoading={ activeSubscriptionFetcher.state === "loading" }>
      <Card size="XL" className="!h-full" fullHeight>
        {/* Top content */}
        <div className="flex flex-col gap-6 flex-1">
          {/* Cancellation or Trial Banner */}
          {(isCancelled || isTrialEnding) && (
            <SubscriptionAlertBanner
              type={isCancelled ? "cancellation" : "trial-ending"}
              date={isCancelled ? activeSubscription?.currentTermEnd : trialInfo?.trialEndTimestamp}
            />
          )}

          <div className="flex flex-col gap-3">
            <p className="text-sm text-gray-600 uppercase tracking-wide">
              CURRENT PLAN
            </p>
            <h3 className="text-2xl font-semibold text-gray-900">
              {currentPlanName || "Free"} Plan
            </h3>

            {renewalMessage && (
              <p className="text-sm text-gray-600">
                {renewalMessage}
              </p>
            )}
          </div>

          {showLegacyPricingNote && <LegacyPricingNote />}

          {formattedPrice && !isFreePlan && (
            <div className="flex items-baseline gap-2">
              {originalPrice && (
                <span className="text-lg font-normal text-gray-400 line-through">
                  {originalPrice}
                </span>
              )}
              <span className="text-4xl font-semibold text-gray-900">
                {formattedPrice}
              </span>
              <span className="text-sm font-normal text-gray-600">
                /{billingPeriod}
              </span>
            </div>
          )}
        </div>

        {/* Bottom buttons */}
        <div className="flex flex-wrap gap-3">
          {/* want to use Yearly instead of annual (being phased out) */}
          {isYearlyPlan(activeSubscription?.planSlug) && (() => {
            const isRenewing = Boolean(activeSubscription?.lastRenewalAttempt);
            const isDisabled = !pricingOptions?.renewal.allowed || user?.isBlocked || isRenewing;

            const tooltipContent = isRenewing
              ? "Your subscription is currently being renewed. Please wait for it to complete."
              : undefined;

            return (
              <PureTooltip content={tooltipContent}>
                <Button
                  text="RESUBSCRIBE TO YOUR PLAN"
                  className="button button-primary w-fit"
                  size="SM"
                  onClick={handleResubscribeClick}
                  disabled={isDisabled}
                />
              </PureTooltip>
            );
          })()}
          <Button
            text="MANAGE PLAN"
            className="button button-secondary w-fit"
            size="SM"
            onClick={handleManagePlanClick}
            disabled={user?.isBlocked}
          />
        </div>
      </Card>
    </LoadingCard>
  );
}
